import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Truck, Package, Clock, CheckCircle, MapPin, Star, 
  User, LogOut, Menu, X, DollarSign, ToggleLeft, ToggleRight,
  Navigation, Shield, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

const statusLabels = {
  pending: 'Pendente',
  accepted: 'Aceite',
  picked_up: 'Em trânsito',
  delivered: 'Entregue',
  cancelled: 'Cancelado'
};

export const DriverDashboard = () => {
  const [availableOrders, setAvailableOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [activeTab, setActiveTab] = useState('available');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    const refreshOnFocus = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', refreshOnFocus);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshOnFocus);
      window.removeEventListener('focus', refreshOnFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    const [availableRes, myOrdersRes, statsRes] = await Promise.allSettled([
      axios.get(`${API}/orders/available`, { headers }),
      axios.get(`${API}/orders`, { headers }),
      axios.get(`${API}/driver/stats`, { headers })
    ]);
    if (availableRes.status === 'fulfilled') setAvailableOrders(availableRes.value.data);
    if (myOrdersRes.status === 'fulfilled') setMyOrders(myOrdersRes.value.data);
    if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    if ([availableRes, myOrdersRes].some(r => r.status === 'rejected' && r.reason?.response?.status === 401)) {
      logout();
      navigate('/login');
      return;
    }
    setLoading(false);
  };

  const toggleAvailability = async () => {
    try {
      await axios.patch(`${API}/users/availability`, 
        { is_available: !isAvailable },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsAvailable(!isAvailable);
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      await axios.patch(`${API}/orders/${orderId}`,
        { status: 'accepted' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
      navigate(`/orders/${orderId}`);
    } catch (error) {
      const status = error.response?.status;
      toast.error(status === 404 || status === 400 || status === 409
        ? 'Este pedido já não está disponível (foi aceite por outro estafeta ou cancelado).'
        : (error.response?.data?.detail || 'Não foi possível aceitar o pedido. Tente novamente.'));
      fetchData();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const activeDeliveries = myOrders.filter(o => ['accepted', 'picked_up'].includes(o.status));

  return (
    <div className="min-h-screen bg-gray-100" data-testid="driver-dashboard">
      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 bg-white p-2 rounded-xl shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-xl transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-tuggo-mint rounded-xl flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">TugGo</span>
          </Link>
        </div>

        {/* Availability Toggle */}
        <div className="px-4 mb-6">
          <button
            onClick={toggleAvailability}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
              isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
            data-testid="availability-toggle"
          >
            <span className="font-medium">{isAvailable ? 'Disponível' : 'Indisponível'}</span>
            {isAvailable ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
          </button>
        </div>

        {/* Stats */}
        <div className="px-4 mb-6 space-y-3">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-tuggo-mint" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats?.total_deliveries || 0}</div>
                <div className="text-sm text-gray-500">Entregas totais</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-green-600" />
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  € {(stats?.total_earnings || 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">Ganhos totais</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Star className="w-5 h-5 text-yellow-500" />
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats?.rating || '5.0'}</div>
                <div className="text-sm text-gray-500">Avaliação</div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-tuggo-mint rounded-full flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900 truncate">{user?.name}</div>
              <div className="text-sm text-gray-500">Estafeta</div>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="driver-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 pt-16 lg:pt-6">
        <div className="max-w-4xl mx-auto">
          {/* Verification Banner */}
          {user && !user.is_verified && (
            <Link 
              to="/verification/driver"
              className="block bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6 hover:bg-yellow-100 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-yellow-800">Complete a sua verificação</div>
                  <div className="text-sm text-yellow-600">
                    {user.verification_status === 'submitted' 
                      ? 'Os seus documentos estão em análise'
                      : 'Verifique a sua identidade para começar a aceitar entregas'}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-yellow-600" />
              </div>
            </Link>
          )}

          {/* Active Delivery Banner */}
          {activeDeliveries.length > 0 && (
            <Link
              to={`/orders/${activeDeliveries[0].id}`}
              className="block bg-tuggo-mint text-white rounded-2xl p-6 mb-6 shadow-lg"
              data-testid="active-delivery-banner"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-80 mb-1">Entrega em curso</div>
                  <div className="text-lg font-bold">{activeDeliveries[0].description}</div>
                  <div className="flex items-center gap-2 mt-2 text-sm">
                    <MapPin className="w-4 h-4" />
                    {activeDeliveries[0].delivery_address.neighborhood}
                  </div>
                </div>
                <Navigation className="w-8 h-8" />
              </div>
            </Link>
          )}

          {/* Tabs */}
          <div className="flex bg-white rounded-full p-1 mb-6 shadow-sm">
            <button
              onClick={() => setActiveTab('available')}
              className={`flex-1 py-3 px-4 rounded-full text-sm font-medium transition-all ${
                activeTab === 'available'
                  ? 'bg-tuggo-mint text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="available-orders-tab"
            >
              Disponíveis ({availableOrders.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-3 px-4 rounded-full text-sm font-medium transition-all ${
                activeTab === 'history'
                  ? 'bg-tuggo-mint text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              data-testid="history-tab"
            >
              Histórico
            </button>
          </div>

          {/* Orders List */}
          {loading ? (
            <div className="bg-white rounded-2xl p-8 text-center text-gray-500">
              Carregando...
            </div>
          ) : activeTab === 'available' ? (
            availableOrders.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhum pedido disponível no momento</p>
                <p className="text-sm text-gray-400 mt-2">Novos pedidos aparecerão aqui automaticamente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {availableOrders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-2xl p-6 shadow-sm"
                    data-testid={`available-order-${order.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                          {order.category}
                        </span>
                        <p className="text-gray-900 font-medium mt-2">{order.description}</p>
                      </div>
                      <div className="text-xl font-bold text-tuggo-mint">
                        € {order.estimated_price.toFixed(2)}
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-start gap-2 text-sm">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                        <div>
                          <div className="text-gray-500">Recolha</div>
                          <div className="text-gray-900">
                            {order.pickup_address.street}, {order.pickup_address.number}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2 text-sm">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5" />
                        <div>
                          <div className="text-gray-500">Entrega</div>
                          <div className="text-gray-900">
                            {order.delivery_address.street}, {order.delivery_address.number}
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      onClick={() => acceptOrder(order.id)}
                      className="w-full bg-tuggo-mint text-white hover:bg-tuggo-mint/90 rounded-full"
                      data-testid={`accept-order-${order.id}`}
                    >
                      Aceitar Entrega
                    </Button>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-4">
              {myOrders.filter(o => o.status === 'delivered').length === 0 ? (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <p className="text-gray-500">Nenhuma entrega concluída ainda</p>
                </div>
              ) : (
                myOrders.filter(o => o.status === 'delivered').map((order) => (
                  <div
                    key={order.id}
                    className="bg-white rounded-2xl p-6 shadow-sm opacity-75"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]}`}>
                          {statusLabels[order.status]}
                        </span>
                        <p className="text-gray-700 mt-2">{order.description}</p>
                      </div>
                      <div className="text-lg font-bold text-gray-600">
                        € {(order.final_price || order.estimated_price).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
